import { addFiltersToApiParam, FilterState } from '@Component/Filter/utils';
import { usePagesPilotFeatureFlags } from '@Hooks/dataFetching/usePagesPilotFeatureFlags';
import { OrderByTypeEmployeeOverview, TableSortState } from '@Hooks/useOrdering';
import { BodyDTO, EmployeeDTO } from '@Models/model.types';
import { APIError, createAPIRequest, ListResponse, useFetchPaginatedState } from '@tlx/astro-shared';
import { postFetcher } from '@Util/api';
import { APIUrls } from '@Util/urls';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';

interface Props {
    filters: FilterState;
    ordering?: TableSortState<OrderByTypeEmployeeOverview>;
    onlyIds?: boolean;
    employeeIdsToShowOnTop?: number[];
    exportProps?: {
        fileName?: string;
        exportColumns?: string;
    };
}

// The return value here has function loadMore and the server provides data that makes it easy to calculate hasMore (happens in useFetchPaginatedState). [Kristoffer Kvello, 2021-08-22]
export function useEmployees(params: Props) {
    const pageSize = 50;
    const getUrl = useGetUrl();

    const response = useSWRInfinite<ListResponse<EmployeeDTO>, APIError>(
        (pageIndex: number, previousPageData: ListResponse<EmployeeDTO>) => {
            if (previousPageData && previousPageData.fullResultSize === 0) {
                return null; // no more data left: this stops the swrinfinite-process
            } else {
                return {
                    url: getUrl({
                        params,
                        from: pageIndex * pageSize,
                        count: pageSize,
                        onlyIds: params.onlyIds,
                    }),
                    params: createBodyDTO(params),
                };
            }
        },
        ({ url, params }) => postFetcher(url, params),
    );
    // response is { data, size, setSize }. The "infinite" part is that we call setSize +1 whenever hasMore is true and we're ready to receive more.
    // Then we call loadMore from the UseFetchPaginatedState value. [Kristoffer Kvello, 2023-08-22]
    return {
        ...useFetchPaginatedState(response),
        mutate: response.mutate,
    };
}

function createBodyDTO(params: Props): BodyDTO {
    const res = {
        query: params.filters.searchFilter.query,
        employeeIdsToShowOnTop: params.employeeIdsToShowOnTop?.join(','),
    };

    return res;
}

export function useDownloadExportedFile() {
    const getUrl = useGetUrl();

    return async (params: Props) => {
        const url = getUrl({
            params,
            from: 0,
            count: 0,
            onlyIds: false,
            exportProps: {
                exportEmployees: true,
                exportColumns: params.exportProps?.exportColumns,
            },
        });
        const request = createAPIRequest(url);

        const response = await fetch(request);

        if (!response.ok) {
            throw new Error('Failed to download file');
        }

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = params.exportProps?.fileName ?? 'employee-overview.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
    };
}

export function useGetAllEmployeeIds(params: Props) {
    const getUrl = useGetUrl();
    return useSWR<ListResponse<EmployeeDTO>>(
        getUrl({
            params,
            count: 0,
            from: 0,
            onlyIds: true,
            filterIsAuthSet: true,
        }),
    );
}

interface UrlProps {
    params: Props;
    from: number;
    count: number;
    onlyIds?: boolean;
    filterIsAuthSet?: boolean;
    exportProps?: {
        exportEmployees?: boolean;
        exportColumns?: string;
    };
}

function useGetUrl() {
    const { data } = usePagesPilotFeatureFlags();

    return ({ params, from, count, onlyIds, filterIsAuthSet, exportProps }: UrlProps): string => {
        const apiParams = new URLSearchParams();
        let url;
        apiParams.set('from', from.toString());

        const baseCommonFields =
            'id, displayName, hasResigned, number, pictureId, isContact, department(id, displayName), isProxy';
        const employeeApproverFields = data?.value.SalaryApprovalAction
            ? ', employeeApprover(id, displayName), employeeApproverRelationType'
            : '';
        const commonFields = baseCommonFields + employeeApproverFields;

        const salaryFields = `${commonFields},socialSecurityNumberOrDNumber, lastActiveEmployment(id, startDate, endDate, latestSalary(id, percentageOfFullTimeEquivalent, annualSalary, monthlySalary, hourlyWage, remunerationType, employmentType, employmentForm, payrollTaxMunicipalityId(id, displayName),occupationCode(id, nameNO,code)), division(id,name,organizationNumber)), deliveryMethodWageSlipString, bankAccountNumber`;
        const detailsFields = `${commonFields}, dateOfBirth, email, phoneNumberMobile, phoneNumberWork, address(id, addressAsString), currentCompanyEmployeeRate(id, hourlyRate, hourlyCost), allowLogin, authReadOnly, vismaConnect2FAactive, isUserAdministrator, allowRegInfo, isPayslipOnly`;
        const taxcardFields = `department(id, displayName), id, displayName, number, taxcard(*, advanceTaxcards(*))`;

        if (exportProps?.exportEmployees) {
            url = APIUrls.EMPLOYEE_OVERVIEW_EXPORT + '?';
            apiParams.set('viewFilter', params.filters.viewFilter.query);
            apiParams.set('query', params.filters.searchFilter.query);
            apiParams.set('fields', 'id');
            apiParams.set('count', String(count));
            apiParams.set('from', String(from));
            apiParams.set('exportColumns', exportProps.exportColumns ?? '');
        } else {
            switch (params.filters.viewFilter.query) {
                case 'salary':
                    url = APIUrls.EMPLOYEE_OVERVIEW_SALARY + '?';
                    if (onlyIds) {
                        apiParams.set('fields', 'id');
                        apiParams.set('count', '0');
                    } else {
                        apiParams.set('fields', salaryFields);
                        apiParams.set('count', count.toString());
                    }
                    break;
                case 'taxcard':
                    url = APIUrls.EMPLOYEE_OVERVIEW_TAXCARD + '?';
                    if (onlyIds) {
                        apiParams.set('fields', 'id');
                        apiParams.set('count', '0');
                    } else {
                        apiParams.set('fields', taxcardFields);
                        apiParams.set('count', count.toString());
                    }
                    if (!apiParams.get('year')) {
                        apiParams.set('year', params.filters.taxcardYearFilter.defaultValue?.[0] ?? '');
                    }
                    break;
                default:
                    url = APIUrls.EMPLOYEE_OVERVIEW_DETAILS + '?';
                    if (onlyIds) {
                        apiParams.set('fields', 'id');
                        apiParams.set('count', '0');
                    } else {
                        apiParams.set('fields', detailsFields);
                        apiParams.set('count', count.toString());
                    }
                    break;
            }
        }

        addFiltersToApiParam(apiParams, params.filters, onlyIds);
        if (filterIsAuthSet) {
            apiParams.append('filterIsAuthSet', 'true');
        }

        if (params.ordering?.sortDirection) {
            apiParams.append('orderDirection', params.ordering.sortDirection === 'ascending' ? 'ASC' : 'DESC');
        }

        if (params.ordering?.sortColumn) {
            apiParams.append('orderBy', params.ordering.sortColumn);
        }

        return url + apiParams.toString();
    };
}
